import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

/**
 * WidgetErrorBoundary — encapsula un widget y muestra un mensaje claro
 * de error en lugar de tirar la página entera. El usuario puede expandir
 * para ver la traza y reportar el bug.
 *
 * Útil para Dashboard, MeetingShow, CompanyShow, etc. — cualquier panel
 * que muestre múltiples piezas y donde una pieza rota no debería matar
 * a las demás.
 */
export function WidgetErrorBoundary({
  children,
  label,
}: {
  children: ReactNode;
  /** Nombre del widget para mostrar en el mensaje. Ej. "Empresas", "Contactos". */
  label?: string;
}) {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <WidgetErrorFallback {...props} label={label} />
      )}
      onError={(error, info) => {
        // eslint-disable-next-line no-console
        console.error(
          `[WidgetErrorBoundary${label ? ` · ${label}` : ""}]`,
          error,
          info.componentStack,
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

function WidgetErrorFallback({
  error,
  resetErrorBoundary,
  label,
}: FallbackProps & { label?: string }) {
  const [expanded, setExpanded] = useState(false);
  const message = (error as Error)?.message ?? String(error);
  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3"
      style={{
        background: "color-mix(in srgb, var(--musco-hot) 8%, var(--musco-panel) 92%)",
        border: "1px solid color-mix(in srgb, var(--musco-hot) 30%, transparent)",
      }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          size={20}
          style={{ color: "var(--musco-hot)", flexShrink: 0, marginTop: 2 }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            Error en {label ?? "este widget"}
          </div>
          <div
            className="text-xs mt-0.5 line-clamp-2"
            style={{ color: "var(--musco-muted)" }}
          >
            {message}
          </div>
        </div>
        <button
          type="button"
          className="musco-btn musco-btn-g"
          style={{ fontSize: 11, padding: "4px 8px" }}
          onClick={resetErrorBoundary}
        >
          Reintentar
        </button>
      </div>
      <button
        type="button"
        className="text-xs underline self-start inline-flex items-center gap-1"
        style={{ color: "var(--musco-muted)" }}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <>
            <ChevronUp size={11} /> Ocultar detalles
          </>
        ) : (
          <>
            <ChevronDown size={11} /> Ver detalles técnicos
          </>
        )}
      </button>
      {expanded ? (
        <pre
          className="text-[10px] overflow-auto p-2 rounded max-h-40"
          style={{
            background: "var(--musco-panel2)",
            color: "var(--musco-muted)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {(error as Error)?.stack ?? message}
        </pre>
      ) : null}
    </div>
  );
}
